import { notificationService } from './notificationService';
import toast from 'react-hot-toast';

class RealTimeNotificationService {
    constructor() {
        this.websocket = null;
        this.eventSource = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.listeners = new Map();
        this.userId = null;
        this.baseURL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    }

    /**
     * Check if user is authenticated
     */
    isUserAuthenticated() {
        try {
            const storageData = localStorage.getItem('loginSuccessNeurulaHealth');
            
            if (storageData) {
                const loginData = JSON.parse(storageData);
                const hasToken = loginData && loginData.authToken;
                return hasToken;
            }
        } catch (error) {
            console.error("Error checking authentication:", error);
        }
        return false;
    }

    /**
     * Initialize real-time notification connection
     */
    async connect(userId) {
        if (!userId) {
            console.error('User ID is required for notification connection');
            return;
        }

        // Check if user is authenticated
        if (!this.isUserAuthenticated()) {
            console.log('User not authenticated, skipping notification connection');
            return;
        }

        this.userId = userId;
        
        // Close existing connection if any
        if (this.websocket || this.eventSource) {
            this.disconnect();
        }

        try {
            // Try WebSocket first, fallback to polling
            if (this.supportsWebSocket()) {
                await this.connectWebSocket();
            } else {
                this.startPolling();
            }
            
        } catch (error) {
            console.error('Failed to connect to notification service:', error);
            this.handleConnectionError();
        }
    }

    /**
     * Start polling for notifications (fallback method)
     */
    startPolling() {
        console.log('Starting polling fallback for notifications');

        // Clear any existing polling interval
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }

        // Adaptive polling: 30s when active, 120s when idle
        const ACTIVE_POLL_INTERVAL = 30000;   // 30 seconds when window focused
        const IDLE_POLL_INTERVAL = 120000;     // 120 seconds when window blurred

        let currentInterval = ACTIVE_POLL_INTERVAL;

        // Track window focus state
        this.isWindowFocused = !document.hidden;

        // Listen for visibility changes
        this.visibilityChangeHandler = () => {
            const wasHidden = !this.isWindowFocused;
            this.isWindowFocused = !document.hidden;

            console.log(`Window visibility changed: ${this.isWindowFocused ? 'visible' : 'hidden'}`);

            // If window just became visible, refresh immediately
            if (wasHidden && this.isWindowFocused) {
                console.log('Window regained focus - refreshing notifications immediately');
                this.checkForNewNotifications();
            }

            // Adjust polling interval based on visibility
            this.updatePollingInterval();
        };

        document.addEventListener('visibilitychange', this.visibilityChangeHandler);

        // Also listen for focus/blur events as backup
        this.focusHandler = () => {
            if (!this.isWindowFocused) {
                console.log('Window gained focus - refreshing notifications');
                this.isWindowFocused = true;
                this.checkForNewNotifications();
                this.updatePollingInterval();
            }
        };

        this.blurHandler = () => {
            if (this.isWindowFocused) {
                console.log('Window lost focus - reducing poll frequency');
                this.isWindowFocused = false;
                this.updatePollingInterval();
            }
        };

        window.addEventListener('focus', this.focusHandler);
        window.addEventListener('blur', this.blurHandler);

        // Update polling interval function
        this.updatePollingInterval = () => {
            const newInterval = this.isWindowFocused ? ACTIVE_POLL_INTERVAL : IDLE_POLL_INTERVAL;

            if (newInterval !== currentInterval) {
                console.log(`Updating poll interval to ${newInterval / 1000}s (window ${this.isWindowFocused ? 'focused' : 'blurred'})`);
                currentInterval = newInterval;

                // Clear and restart with new interval
                if (this.pollingInterval) {
                    clearInterval(this.pollingInterval);
                }

                this.pollingInterval = setInterval(async () => {
                    try {
                        await this.checkForNewNotifications();
                    } catch (error) {
                        console.error('Error polling for notifications:', error);
                    }
                }, currentInterval);
            }
        };

        // Start initial polling
        this.pollingInterval = setInterval(async () => {
            try {
                await this.checkForNewNotifications();
            } catch (error) {
                console.error('Error polling for notifications:', error);
            }
        }, currentInterval);

        // Initial check
        this.checkForNewNotifications();
        this.isConnected = true;

        this.emit('connection_status', {
            isConnected: true,
            message: 'Connected via polling (WebSocket unavailable)'
        });
    }

    /**
     * Check for new notifications
     */
    async checkForNewNotifications() {
        try {
            const response = await notificationService.getNotifications({
                recipient_id: this.userId,
                unread_only: true,
                page: 1,
                page_size: 20
            });

            if (response.success && response.data) {
                const notifications = response.data.notifications || [];
                
                // Check for new notifications since last check
                const newNotifications = notifications.filter(notification => 
                    !this.hasBeenProcessed(notification.notification_id)
                );

                newNotifications.forEach(notification => {
                    this.handleNewNotification(notification);
                    this.markAsProcessed(notification.notification_id);
                });
            }
        } catch (error) {
            console.error('Error checking for new notifications:', error);
        }
    }

    /**
     * Check if WebSocket is supported
     */
    supportsWebSocket() {
        return 'WebSocket' in window || 'MozWebSocket' in window;
    }

    /**
     * Connect using WebSocket
     */
    async connectWebSocket() {
        try {
            // Get auth token from localStorage using the same key as the API client
            let loginData = null;
            try {
                const storageData = localStorage.getItem('loginSuccessNeurulaHealth');
                if (storageData) {
                    loginData = JSON.parse(storageData);
                }
            } catch (error) {
                console.error("Error parsing login data:", error);
            }

            if (!loginData || !loginData.authToken) {
                console.error('No authentication token available');
                // Fallback to polling instead of failing completely
                this.startPolling();
                return;
            }

            // Convert HTTP URL to WebSocket URL with token
            const wsUrl = this.baseURL.replace('http', 'ws') + `/ws/notifications/${this.userId}?token=${loginData.authToken}`;
            console.log('Attempting WebSocket connection to:', wsUrl);
            
            this.websocket = new WebSocket(wsUrl);
            
            this.websocket.onopen = () => {
                console.log('WebSocket notification connection established');
                this.isConnected = true;
                this.reconnectAttempts = 0;
                this.emit('connected');
                this.emit('connection_status', {
                    isConnected: true,
                    message: 'Connected to notification service'
                });
            };

            this.websocket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.handleWebSocketMessage(data);
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            this.websocket.onclose = (event) => {
                console.log('WebSocket connection closed:', event.code, event.reason);
                this.isConnected = false;
                
                this.emit('connection_status', {
                    isConnected: false,
                    message: 'Connection lost. Notifications may be delayed.',
                    code: event.code,
                    reason: event.reason
                });
                
                // Attempt to reconnect if not a normal closure
                if (event.code !== 1000) {
                    this.handleConnectionError();
                }
            };

            this.websocket.onerror = (error) => {
                console.error('WebSocket error:', error);
                this.handleWebSocketError(error);
            };

        } catch (error) {
            console.error('Failed to establish WebSocket connection:', error);
            console.log('Falling back to polling method');
            // Fallback to polling
            this.startPolling();
        }
    }

    /**
     * Handle WebSocket connection errors and fallback to polling
     */
    handleWebSocketError(error) {
        console.error('WebSocket connection error:', error);
        console.log('Falling back to polling due to WebSocket error');
        
        // Close WebSocket if it exists
        if (this.websocket) {
            this.websocket.close();
            this.websocket = null;
        }
        
        // Fallback to polling
        this.startPolling();
    }

    /**
     * Handle WebSocket messages
     */
    handleWebSocketMessage(message) {
        switch (message.type) {
            case 'connection_established':
                console.log('WebSocket connection established:', message.message);
                break;
                
            case 'notification':
                console.log('New notification received:', message.data);
                this.handleNewNotification(message.data);
                break;
                
            case 'unread_count':
                console.log('Unread count updated:', message.count);
                this.emit('unreadCountChanged', message.count);
                break;
                
            case 'pong':
                // Handle ping/pong for connection health
                break;
                
            default:
                console.log('Unknown WebSocket message type:', message.type);
        }
    }

    /**
     * Handle new notification
     */
    handleNewNotification(notification) {
        // Emit to listeners
        this.emit('notification', notification);

        // Show toast for high priority notifications
        if (notification.priority === 'HIGH' || notification.priority === 'CRITICAL' || notification.priority === 'URGENT') {
            this.showToastNotification(notification);
        }

        // Update notification count
        this.emit('unreadCountChanged');
    }

    /**
     * Show toast notification
     */
    showToastNotification(notification) {
        const toastOptions = {
            duration: notification.priority === 'CRITICAL' ? 0 : 6000, // Critical notifications don't auto-dismiss
            style: {
                background: this.getNotificationColor(notification.priority),
                color: 'white',
                maxWidth: '400px'
            },
            position: 'top-right'
        };

        if (notification.priority === 'CRITICAL') {
            toast.error(`🚨 ${notification.title}\n${notification.message}`, toastOptions);
        } else if (notification.priority === 'HIGH' || notification.priority === 'URGENT') {
            toast.error(`⚠️ ${notification.title}\n${notification.message}`, toastOptions);
        } else {
            toast.success(`📢 ${notification.title}\n${notification.message}`, toastOptions);
        }
    }

    /**
     * Get notification color based on priority
     */
    getNotificationColor(priority) {
        const colors = {
            'CRITICAL': '#dc2626',
            'URGENT': '#ea580c',
            'HIGH': '#d97706',
            'NORMAL': '#059669',
            'LOW': '#0891b2'
        };
        return colors[priority] || colors['NORMAL'];
    }

    /**
     * Check if notification has been processed
     */
    hasBeenProcessed(notificationId) {
        const processedNotifications = JSON.parse(
            localStorage.getItem('processedNotifications') || '[]'
        );
        return processedNotifications.includes(notificationId);
    }

    /**
     * Mark notification as processed
     */
    markAsProcessed(notificationId) {
        const processedNotifications = JSON.parse(
            localStorage.getItem('processedNotifications') || '[]'
        );
        
        // Keep only last 1000 processed notifications to prevent localStorage bloat
        if (processedNotifications.length > 1000) {
            processedNotifications.splice(0, processedNotifications.length - 1000);
        }
        
        processedNotifications.push(notificationId);
        localStorage.setItem('processedNotifications', JSON.stringify(processedNotifications));
    }

    /**
     * Handle connection errors
     */
    handleConnectionError() {
        this.isConnected = false;
        
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }

        // Attempt to reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
            
            setTimeout(() => {
                this.connect(this.userId);
            }, this.reconnectDelay * this.reconnectAttempts);
        } else {
            console.error('Max reconnection attempts reached');
            this.emit('connection_lost', {
                message: 'Connection lost. Notifications may be delayed.',
                canRetry: false
            });
        }
    }

    /**
     * Add event listener
     */
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }

    /**
     * Remove event listener
     */
    off(event, callback) {
        if (this.listeners.has(event)) {
            const callbacks = this.listeners.get(event);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }

    /**
     * Emit event
     */
    emit(event, data) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('Error in notification listener:', error);
                }
            });
        }
    }

    /**
     * Disconnect from notification service
     */
    disconnect() {
        this.isConnected = false;

        if (this.websocket) {
            this.websocket.close(1000, 'User disconnect');
            this.websocket = null;
        }

        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }

        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }

        // Cleanup visibility and focus event listeners
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
            this.visibilityChangeHandler = null;
        }

        if (this.focusHandler) {
            window.removeEventListener('focus', this.focusHandler);
            this.focusHandler = null;
        }

        if (this.blurHandler) {
            window.removeEventListener('blur', this.blurHandler);
            this.blurHandler = null;
        }

        this.listeners.clear();
        console.log('Disconnected from notification service');
    }

    /**
     * Get connection status
     */
    getConnectionStatus() {
        return {
            isConnected: this.isConnected,
            userId: this.userId,
            reconnectAttempts: this.reconnectAttempts,
            isAuthenticated: this.isUserAuthenticated()
        };
    }

    /**
     * Retry connection when authentication state changes
     */
    async retryConnection() {
        if (this.userId && this.isUserAuthenticated() && !this.isConnected) {
            console.log('Retrying notification connection after authentication');
            await this.connect(this.userId);
        }
    }
}

// Export singleton instance
export const realTimeNotificationService = new RealTimeNotificationService();
export default realTimeNotificationService;